import { createContext, useContext, useEffect, useState } from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import axios from 'axios';
import MessagebarContext from "./MessagebarContext";
import {useTranslation} from "react-i18next";
import WhiteLabelContext from "./WhiteLabelContext";
import { findTranslation } from "../utils/helpers";
import Bugsnag from '@bugsnag/js';

export const AUTH_STATUS = {
  loading: 'LOADING',
  loggedin: 'LOGGEDIN',
  loggedout: 'LOGGEDOUT',
};

const INITIAL_PAGE_BLACKLIST = [
  "/not-found",
  "/login",
  "/password/reset/",
  "/forgot-password",
  "/switch-mfa",
  "/mfa",
  "/recovery-code",
  "/aanmelden/met-code/",
  "/request-invite",
  "/sign-request-signed",
  "/sign-request/stiply-redirect",
  "/dossier-select"
];

const UNAUTHORIZED = 401;

const UserContext = createContext();

let initPathname = null;
export const UserProvider = (props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useLocation();
  const { setSuccesMessage } = useContext(MessagebarContext);
  const { language } = useContext(WhiteLabelContext);

  const [user, setUserData] = useState(null);
  const [enabledModules, setEnabledModules] = useState({});
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.loading);
  const [changedDataWithoutSaving, setChangedDataWithoutSaving] = useState(
    false
  );

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  useEffect(() => {
    const searchParams = new URLSearchParams(params.search);

    if (!INITIAL_PAGE_BLACKLIST.some(listedPage => params.pathname.startsWith(listedPage))) {
      initPathname = params;
    }

    if (searchParams.get('redirect')
      && !INITIAL_PAGE_BLACKLIST.some(listedPage => searchParams.get('redirect').startsWith(listedPage))
    ) {
      initPathname = {
        pathname: searchParams.get('redirect')
      };
    }

    axios({
      method: 'get',
      url: '/who-am-i',
      data: {},
    })
      .then(({ data: { user } }) => {
        if (user) {
          setUser(user);

          if (initPathname !== null) {
            navigate(initPathname);
          }
          const { sso, full_name, email } = user;
          if (sso) {
            setSuccesMessage(t('Ingelogd als {{fullName}} ({{email}})', {fullName: full_name, email}));
          }
        }
      })
      .catch((err) => {
        if (err.response?.status === UNAUTHORIZED) {
          setAuthStatus(AUTH_STATUS.loggedout);
        } else {
          console.error(err);
        }
      });

    if (searchParams.get('email-confirm')) {
      setSuccesMessage(t('Je e-mailadres is geverifieerd!'));
    }
  }, []);

  axios.interceptors.response.use(null, (error) => {
    if (error.response?.status === UNAUTHORIZED) {
      if (error.response.data?.authenticator_type === 'otp') {
        navigate('/mfa');
      } else if (error.response.data?.authenticator_type === 'recovery-codes') {
        navigate('/recovery-code');
      } else {
        if (!error.response.headers['auth-username']) {
          setUserData(null);
          setAuthStatus(AUTH_STATUS.loggedout);

          if (!INITIAL_PAGE_BLACKLIST.some(noRedirectPage => params.pathname.includes(noRedirectPage))) {
            initPathname = params;
            navigate('/login');
          }
        }
      }
    }

    return Promise.reject(error);
  });

  const logoutUser = () => {
    axios({ method: 'get', url: '/logout', data: {} }).then(() => {
      setUserData(null);
      setAuthStatus(AUTH_STATUS.loggedout);
      initPathname = null;
      navigate('/login');
    });
  };

  const setUser = (data) => {
    setUserData(data);
    setAuthStatus(AUTH_STATUS.loggedin);

    if (data?.language) i18n.changeLanguage(data.language);
    if (Bugsnag.isStarted()) Bugsnag.setUser(data.uuid);
  };

  const isAuthenticated = () => !!user;
  const linkedDossiers = user?.linked_dossiers || [];
  const dossierName = findTranslation(user?.dossier, linkedDossiers);

  return (
    <UserContext.Provider
      value={{
        user,
        linkedDossiers,
        dossierName,
        setUser,
        logoutUser,
        isAuthenticated,
        authStatus,
        setAuthStatus,
        changedDataWithoutSaving,
        setChangedDataWithoutSaving,
        initPathname,
        enabledModules,
        setEnabledModules
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export const UserConsumer = UserContext.Consumer;

export default UserContext;
